
/**
 * Execute identifiable tasks, allowing us to check what tasks are already
 * running, and piggyback on the result of already running tasks.
 *
 * This is useful for preventing duplicate work form being performed, and to not
 * start new work if a certain concurrency limit has been reached.
 *
 * To allow piggybacking on existing tasks, all task functions provided for a
 * given id is expected to be functionally equivalent.
 *
 * TODO: Implement TaskTracking<T> based on Piggyback<T>.
 */
export class TaskTracking<T> {
  constructor(private completionHandler?: (concurrency: number, cause: any) => void) {
  }

  private ongoing = new Map<string, Promise<T>>();
  runOrPiggyback(id: string, func: () => Promise<T>): Promise<T> {
    const started = this.ongoing.get(id);
    if (started != null) {
      return started;
    }

    return this.runTracked(id, func);
  }

  private async runTracked(id: string, func: () => Promise<T>) {
    const promise = func();
    this.ongoing.set(id, promise);
    let error: any;
    let result: T | undefined;
    try {
      result = await promise;
    } catch (ex) {
      error = ex;
    }

    this.ongoing.delete(id);
    if (this.completionHandler) {
      try {
        this.completionHandler(this.concurrency, error);
      } catch (ex) {
        console.error('ConcurrencyTracker completionHandler error', ex);
      }
    }

    if (error) {
      throw error;
    }

    return result as T;
  }

  get concurrency() {
    return this.ongoing.size;
  }

  isOngoing(id: string) {
    return this.ongoing.has(id);
  }

  toString() {
    return [...this.ongoing.keys()].join(', ');
  }
}

/**
 * Execute a specific task, allowing us piggyback on the result of an already
 * running tasks if present.
 *
 * This is useful for preventing duplicate work form being performed.
 */
export class Piggyback<T> {
  started: Promise<T> | null = null;

  runOrPiggyback(func: () => Promise<T>): Promise<T> {
    return this.started ?? this.runTracked(func);
  }

  private async runTracked(func: () => Promise<T>) {
    this.started = func();
    try {
      return await this.started;
    } finally {
      this.started = null;
    }
  }
}
